import { useEffect, useRef } from 'react';
import { MessageSquare, Palette, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/reui/badge';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { EstadoChip } from '@/components/rdt/estado-chip';
import { esPendiente, fmtMonto, fmtMontoCorto, fmtPct, ordenarRdts, periodoRdt, perLabel, ultimoChequeo } from '@/components/rdt/rdt-domain';
import { AvatarPersona } from '@/components/rdt/avatar-persona';
import { NroRdt } from '@/components/rdt/nro-rdt';
import { useMe } from '@/hooks/use-me';
import type { RdtResumen } from '@/types/api';

interface CardProps {
  rdt: RdtResumen;
  selected: boolean;
  onSelect: () => void;
  cardRef?: (el: HTMLElement | null) => void;
}

// Card de una RDT en la columna izquierda — período (o "chq. hasta X" si
// todavía no saltó), último comentario a modo de preview, chip de estado,
// chip "Archivada" (punteado) y conteo de comentarios + avatar del
// responsable (foto real con fallback a iniciales, mismo patrón que
// obra-card.tsx / UserAvatar). El contenedor sigue siendo propio (Ajuste
// C-13 del plan: no entra tal cual en `Item` — el chequeo de wrap de la fila
// de chips y el agrupamiento condicional monto+% no tienen equivalente en
// su contrato fijo de título/descripción).
function RdtListCardItem({ rdt, selected, onSelect, cardRef }: CardProps) {
  const esDefinitiva = rdt.tipo === 'Definitiva';
  const uc = ultimoChequeo(rdt);
  let periodo: string | null;
  let periodoTitle: string | undefined;

  if (esDefinitiva) {
    // Definitivas no chequean — el período de gestión sale del monto
    // vigente (montos viene last-wins, el primero es el vigente), no del
    // salto (siempre null acá).
    const periodoMonto = rdt.montos[0]?.periodo;
    periodo = periodoMonto ? perLabel(periodoMonto) : '—';
    periodoTitle = periodoMonto ? 'Período del monto informado vigente' : 'Sin monto informado';
  } else {
    periodo = periodoRdt(rdt);
    if (!rdt.salto && uc) {
      periodo = `chq ${perLabel(uc.periodo)}`;
      periodoTitle = `Chequeada hasta ${perLabel(uc.periodo)} — sin salto`;
    } else if (!periodo) {
      periodo = '—';
      periodoTitle = 'Sin chequeos registrados';
    } else {
      // Procedencia del período (pregunta de Juan 2026-07-10): mes de índices
      // del salto; en migradas, derivado de la fecha de índices histórica.
      periodoTitle = rdt.migrada && !rdt.salto
        ? 'Período de índices del salto — derivado de la fecha de índices histórica del legacy'
        : 'Período de índices del salto';
    }
  }

  const responsableNombre = rdt.responsable?.nombre ?? rdt.responsableHistorico ?? null;
  // % de la card (Provisorias): el del chequeo que saltó y, si ese no lo
  // informó (migradas: valor_pct NULL), el histórico del legacy — la misma
  // proyección que procedenciaPct() hace para la lápida (ficha.tsx).
  const pctCard = !esDefinitiva ? (rdt.salto?.valorPct ?? rdt.indicePct) : null;
  // % de la card (Definitivas, Juan QA 2026-09-14: SOFSE/ADIF por FRI/% en
  // vez de monto) — `indicePct` ya viene materializado del vigente para
  // Definitivas (backend, "materializarMonto" §4), sin el fallback al
  // salto que no aplica acá (una Definitiva no chequea, `rdt.salto` es
  // siempre null).
  const pctCardDefinitiva = esDefinitiva ? rdt.indicePct : null;

  return (
    <article
      ref={cardRef}
      aria-current={selected || undefined}
      role="button"
      tabIndex={0}
      aria-label={`Rede ${rdt.nro} — ${rdt.tipo}`}
      onClick={onSelect}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'relative flex cursor-pointer flex-col gap-1.5 rounded-[11px] border border-border-subtle bg-surface-card px-3 pb-2.5 pt-[11px] shadow-[0_1px_2px_rgba(20,20,22,0.04)] transition-[background-color,border-color,box-shadow] duration-[120ms] ease-out hover:border-border-strong hover:bg-surface-2 hover:shadow-[0_2px_8px_rgba(20,20,22,0.07)]',
        rdt.archivada && 'border-dashed opacity-75',
        selected &&
          'border-primary/55 bg-surface-2 ring-1 ring-primary/40 hover:border-primary/55',
      )}
    >
      <div className="flex min-w-0 items-baseline gap-[7px]">
        <NroRdt
          nro={rdt.nro}
          tipo={rdt.tipo}
          className="whitespace-nowrap font-mono text-sm font-bold leading-[1.5] text-foreground"
        />
        <span className="truncate text-[11px] text-muted-foreground">{rdt.tipo}</span>
        {periodoTitle ? (
          <Tooltip>
            <TooltipTrigger
              render={<span className="ml-auto whitespace-nowrap font-mono text-[11px] tabular-nums text-text-tertiary" />}
            >
              {periodo}
            </TooltipTrigger>
            <TooltipContent>{periodoTitle}</TooltipContent>
          </Tooltip>
        ) : (
          <span className="ml-auto whitespace-nowrap font-mono text-[11px] tabular-nums text-text-tertiary">{periodo}</span>
        )}
      </div>

      {rdt.ultimoComentario ? (
        <p className="m-0 line-clamp-2 min-h-[17px] text-xs leading-[1.4] text-muted-foreground">{rdt.ultimoComentario.texto}</p>
      ) : (
        <p className="m-0 min-h-[17px] text-xs italic leading-[1.4] text-text-tertiary">Sin comentarios</p>
      )}

      {/* Fila con wrap (fix hallazgo 3, QA ronda 2 Juan 2026-09-15: en la
          Definitiva #QA1 de 1426, chip "Entregado al cliente" + monto + %
          hacía que el monto — antes el único hijo encogible, min-w-0
          truncate, cinturón anti-desborde de 0.10.3 — se encogiera a CERO y
          desapareciera aunque rdt.monto tuviera valor). Regla nueva: nada
          de datos se trunca; si no entra todo en una línea, la fila hace
          wrap (flex-wrap + gap-y) en vez de sacrificar un dato. Monto y %
          quedan agrupados en un solo span flex-none para que, si no
          entran, salten juntos a la segunda línea — nunca uno sin el otro
          ni uno truncado. Cuando todo entra en una línea el resultado es
          visualmente idéntico al de antes. */}
      <div className="mt-px flex flex-wrap items-center gap-x-1.5 gap-y-1 min-w-0">
        {/* esperandoPoliza pisa el chip "Aprobado" EN TODOS LADOS (ajuste 1
            de Juan, 2026-07-11) — antes solo la ficha lo mostraba, esta
            card decía "Aprobado" a secas. */}
        <EstadoChip estado={rdt.estado} esperandoPoliza={rdt.esperandoPoliza} />
        {rdt.archivada && (
          <Badge variant="outline" size="default" className="border-dashed text-text-tertiary">
            Archivada
          </Badge>
        )}
        {(pctCard != null || (esDefinitiva && (rdt.monto != null || pctCardDefinitiva != null))) && (
          // Grupo único monto+% (hallazgo 3 de arriba): en la Provisoria es
          // solo el %; en la Definitiva, monto y % viajan juntos — si la
          // fila no les da lugar, saltan los dos juntos a la línea
          // siguiente en vez de truncarse o desaparecer por separado.
          <span className="inline-flex flex-none items-center gap-1.5 whitespace-nowrap">
            {pctCard != null && (
              // % proyectado del salto (o el histórico del legacy en
              // migradas: la misma proyección que la lápida) — pedido de
              // Juan (QA en vivo 2026-09-14): la card de la Provisoria
              // muestra el % donde la Definitiva muestra el monto.
              <span className="text-[10.5px] font-semibold tabular-nums text-foreground">{fmtPct.format(pctCard)} %</span>
            )}
            {esDefinitiva && rdt.monto != null && (
              // Abreviado acá (mismo criterio que "Oferta" en
              // header-obra.tsx, fmtMontoCorto en rdt-domain.ts); el monto
              // completo sigue disponible en el tooltip, en la ficha y en
              // el hilo de Actividad.
              <Tooltip>
                <TooltipTrigger render={<span className="text-[10.5px] font-semibold tabular-nums text-foreground" />}>
                  {fmtMontoCorto(rdt.monto)}
                </TooltipTrigger>
                <TooltipContent>{fmtMonto.format(rdt.monto)}</TooltipContent>
              </Tooltip>
            )}
            {pctCardDefinitiva != null && (
              // % de la Definitiva (SOFSE/ADIF por FRI/%, mismo QA): si hay
              // monto y %, el monto ya salió primero, dentro del mismo grupo.
              <span className="text-[10.5px] font-semibold tabular-nums text-foreground">{fmtPct.format(pctCardDefinitiva)} %</span>
            )}
          </span>
        )}
        <span className="ml-auto inline-flex flex-none items-center gap-1.5">
          {/* Contador + avatar en un solo grupo ml-auto (hallazgo 3): así,
              entren en una línea o salten a la segunda junto con el resto,
              siempre quedan pegados a la derecha entre sí. */}
          <Tooltip>
            <TooltipTrigger
              render={
                <span className="inline-flex flex-none items-center gap-[3px] whitespace-nowrap text-[10.5px] tabular-nums text-text-tertiary" />
              }
            >
              <MessageSquare className="h-[11px] w-[11px]" aria-hidden="true" />
              {rdt.comentariosCount}
            </TooltipTrigger>
            <TooltipContent>{`${rdt.comentariosCount} comentarios`}</TooltipContent>
          </Tooltip>
          {responsableNombre && (
            <AvatarPersona nombre={responsableNombre} fotoUrl={rdt.responsable?.fotoUrl ?? null} size={21} />
          )}
        </span>
      </div>
    </article>
  );
}

interface ColumnProps {
  rdts: RdtResumen[];
  pendientes: RdtResumen[];
  verTodas: boolean;
  onVerTodasChange: (value: boolean) => void;
  selectedId: number | null;
  // Id a hacer scrollIntoView en esta columna — distinto de `selectedId`:
  // solo se setea cuando la selección viene de navegación (⌘K expedientes o
  // URL con ?rdt=), nunca de un click manual en una card (ver obra-detalle.tsx).
  scrollToId: number | null;
  onRequestSelect: (id: number) => void;
  onNueva: () => void;
}

/** Copy del empty state "Sin RDTs pendientes" — diferencia «todas
 *  facturadas» de «todas archivadas» de la mezcla (punto 2 v0.9.0: antes
 *  metía los tres casos en la misma bolsa genérica, generando la confusión
 *  de que "algo se archivó solo" cuando en realidad el filtro Pendientes
 *  esconde facturadas y archivadas por igual). */
function copyVaciaPendientes(rdts: RdtResumen[]): string {
  const noPendientes = rdts.filter(r => !esPendiente(r));
  const hayArchivadas = noPendientes.some(r => r.archivada);
  const hayFacturadasSinArchivar = noPendientes.some(r => !r.archivada);
  if (hayArchivadas && hayFacturadasSinArchivar) return 'Todas las redeterminaciones de esta obra están facturadas o archivadas.';
  if (hayArchivadas) return 'Todas las redeterminaciones de esta obra están archivadas.';
  if (hayFacturadasSinArchivar) return 'Todas las redeterminaciones de esta obra están facturadas.';
  return 'Todas las redeterminaciones de esta obra están facturadas o archivadas.';
}

// Vacío compacto para la columna angosta (280–316px) — misma pareja
// título/bajada de siempre, sobre `Empty` de ui/ con la densidad ajustada al
// contenedor (el `p-12`/`text-lg` por defecto de Empty está pensado para un
// panel entero, no para esta columna).
function ListaVacia({ titulo, descripcion, accion }: { titulo: string; descripcion: string; accion?: { label: string; onClick: () => void } }) {
  return (
    <Empty className="min-h-0 gap-1.5 rounded-none border-none p-4 py-[46px]">
      <EmptyHeader className="max-w-none gap-1.5">
        <EmptyTitle className="text-[13.5px] font-semibold">{titulo}</EmptyTitle>
        <EmptyDescription className="text-xs">{descripcion}</EmptyDescription>
      </EmptyHeader>
      {accion && (
        <EmptyContent className="max-w-none">
          <Button type="button" variant="ghost" size="sm" onClick={accion.onClick}>
            {accion.label}
          </Button>
        </EmptyContent>
      )}
    </Empty>
  );
}

// Columna izquierda completa: pestañas Pendientes/Todas + conteo + «Nueva» +
// lista con scroll propio (col-list del preview). El toggle NO pasa por la
// guarda de "descartar edición" — solo cambiar de RDT seleccionada la
// dispara (la resuelve la página vía onRequestSelect).
//
// R3-C (plan_migracion_reui.md, Ajuste 10): Pendientes/Todas pasan de un
// `role=group` con 2 botones `aria-pressed` a `Tabs` de ui/ (c-tabs-9,
// https://reui.io/preview/base/components/c-tabs-9?ref=mcp) con
// `TabsList activateOnFocus` (←/→ cambian la vista, como el radiogroup
// aprobado) y `TabsContent tabIndex={-1}` en cada panel.
export function RdtListColumn({
  rdts,
  pendientes,
  verTodas,
  onVerTodasChange,
  selectedId,
  scrollToId,
  onRequestSelect,
  onNueva,
}: ColumnProps) {
  const pendientesOrdenadas = ordenarRdts(pendientes);
  const todasOrdenadas = ordenarRdts(rdts);
  const visibles = verTodas ? todasOrdenadas : pendientesOrdenadas;
  // «Nueva» solo para gestor/admin (viewers no tienen el endpoint habilitado
  // — ocultar, no deshabilitar; mismo criterio que la botonera de la ficha).
  const { data: me } = useMe();
  const puedeEscribir = me?.rol === 'gestor' || me?.rol === 'admin';

  // Refs de las cards por id — solo para el scroll-a-la-vista de abajo (no
  // hay drag/animación acá, así que un Map alcanza sin remontar nada).
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());

  // Scroll-a-la-vista de la card recién seleccionada por navegación (⌘K
  // expedientes / URL con ?rdt=) — bug reportado por Juan: buscó un
  // expediente, cayó en la obra 1130 con la rede #28 marcada, pero la card
  // quedaba fuera de la vista en obras con 30+ redes. `scrollToId` (y no
  // `selectedId`) es la dependencia a propósito: un click manual en una card
  // ya visible nunca la mueve de lugar.
  useEffect(() => {
    if (scrollToId == null) return;
    const el = itemRefs.current.get(scrollToId);
    if (!el) return;
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: prefersReduced ? 'auto' : 'smooth', block: 'center' });
  }, [scrollToId]);

  function renderCards(lista: RdtResumen[]) {
    return lista.map(r => (
      <RdtListCardItem
        key={r.id}
        rdt={r}
        selected={r.id === selectedId}
        onSelect={() => onRequestSelect(r.id)}
        cardRef={el => {
          if (el) itemRefs.current.set(r.id, el);
          else itemRefs.current.delete(r.id);
        }}
      />
    ));
  }

  return (
    <Tabs
      value={verTodas ? 'todas' : 'pendientes'}
      onValueChange={v => onVerTodasChange(v === 'todas')}
      aria-label="Redeterminaciones de la obra"
      className="min-h-0 w-[316px] flex-none flex-col border-r border-border-subtle max-[1440px]:w-[280px]"
    >
      {/* Cabecera en dos filas, en TODOS los anchos (V1, boceto de Juan del
          28/9). Orden de la variante B de la galería de O9 (Juan, 29/9,
          claude.ai/artifact/CwTG3sCKDgtdsGC4FXireT): fila 1, los dos controles
          (pestañas Pendientes/Todas a la izquierda, «+ Nueva» a la derecha);
          fila 2, lo que resume esa elección: el contador «N de M» con la
          leyenda de colores pegada. Es el orden de la rc.1 (pestañas primero).
          El contador vive en su propio span y nunca comparte línea con las
          pestañas, así que no se parte. */}
      {/* pb-1.5 + el pt-1.5 de las listas (Juan, 28/9): el scroller recorta el anillo de foco
          (2 px + 2 px de separación) de la primera card si arranca a menos de 4 px; el aire
          total entre la cabecera y la primera card sigue en 12 px. */}
      <div className="flex flex-none flex-col gap-1.5 px-3.5 pb-1.5 pt-[13px]">
        <div className="flex items-center gap-1.5">
          {/* Los TabsTrigger de ReUI traen text-sm; acá van a los
              px-2.5/py-1/text-xs del toggle de la rc.1, sin tocar ui/tabs.tsx
              (compartido). */}
          <TabsList aria-label="Filtro de RDTs" activateOnFocus>
            <TabsTrigger value="pendientes" className="px-2.5 py-1 text-xs">Pendientes</TabsTrigger>
            <TabsTrigger value="todas" className="px-2.5 py-1 text-xs">Todas</TabsTrigger>
          </TabsList>
          {puedeEscribir && (
            // Píldora «+ Nueva» (antes: círculo con solo «+», sin lugar para
            // texto en una sola fila con pestañas, contador y leyenda). En
            // dos filas entra el texto — mismo tamaño/estilo que
            // «+ Paso» de la Actividad (actividad.tsx ~1365-1377: Button
            // default, h-auto gap-[5px] rounded-full px-[11px] py-[5px]
            // text-xs font-[650], Plus 13px strokeWidth 2.4), mismo patrón
            // para las dos acciones de «agregar» de las dos columnas. Sin
            // Tooltip: el texto visible «Nueva» ya dice lo que decía el
            // tooltip; el aria-label completa el nombre accesible.
            <Button
              type="button"
              size="sm"
              onClick={onNueva}
              aria-label="Nueva redeterminación"
              className="ml-auto h-auto gap-[5px] rounded-full px-[11px] py-[5px] text-xs font-[650]"
            >
              <Plus className="h-[13px] w-[13px] flex-none" strokeWidth={2.4} aria-hidden="true" />
              Nueva
            </Button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <span className="whitespace-nowrap shrink-0 text-xs tabular-nums text-text-tertiary">
            {visibles.length} de {rdts.length}
          </span>
          <LeyendaColores />
        </div>
      </div>

      <TabsContent
        value="pendientes"
        tabIndex={-1}
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3.5 pb-4 pt-1.5"
      >
        {pendientesOrdenadas.length === 0 ? (
          <ListaVacia
            titulo="Sin RDTs pendientes"
            descripcion={copyVaciaPendientes(rdts)}
            accion={{ label: 'Ver todas', onClick: () => onVerTodasChange(true) }}
          />
        ) : (
          renderCards(pendientesOrdenadas)
        )}
      </TabsContent>
      <TabsContent
        value="todas"
        tabIndex={-1}
        className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto px-3.5 pb-4 pt-1.5"
      >
        {todasOrdenadas.length === 0 ? (
          <ListaVacia titulo="Sin redeterminaciones" descripcion="Esta obra todavía no tiene redeterminaciones cargadas." />
        ) : (
          renderCards(todasOrdenadas)
        )}
      </TabsContent>
    </Tabs>
  );
}

// Leyenda «Colores por estado» (Tramo C, paleta B v0.9.0): la paleta volvió
// a la semántica del legacy (verde = Facturado) y este popover es lo que la
// enseña — clave para quien traía la memoria de la v3, donde el verde
// significaba lo mismo pero el resto cambió. Los chips se renderizan con el
// componente REAL (EstadoChip): la leyenda no puede mentir aunque la paleta
// vuelva a cambiar.
const LEYENDA_ESTADOS: { estado: string; hint: string }[] = [
  { estado: 'A chequear', hint: 'falta chequear índices del período' },
  { estado: 'Sin salto', hint: 'chequeada, no saltó' },
  { estado: 'Saltó', hint: 'saltó el gatillo — en gestión' },
  { estado: 'A calcular', hint: 'Definitiva sin monto informado' },
  { estado: 'Calculado', hint: 'Definitiva con monto informado' },
  { estado: 'Entregado a obra', hint: 'documentación entregada' },
  { estado: 'Aprobado', hint: 'aprobada por el cliente' },
  { estado: 'A la espera de póliza', hint: 'aprobada, falta la póliza EC' },
  { estado: 'Facturado', hint: 'ciclo cerrado' },
];

function LeyendaColores() {
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger
          render={
            // O9 (Juan, 29/9): el gris de la rc.1 (el del contador; con hover,
            // el del texto) y 20 px, a 8 px del contador. En táctil conserva
            // los 20 px y el blanco de 44 px lo da un after: invisible, así
            // no se separa del contador (con el size-11 del cva quedaba a 22 px).
            <PopoverTrigger
              render={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Colores por estado"
                  className="size-5 text-text-tertiary hover:text-foreground [@media(hover:none)]:size-5 [@media(hover:none)]:after:absolute [@media(hover:none)]:after:-inset-[12px] [@media(hover:none)]:after:content-['']"
                />
              }
            />
          }
        >
          <Palette className="h-3.5 w-3.5" strokeWidth={2} aria-hidden="true" />
        </TooltipTrigger>
        <TooltipContent>Colores por estado</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" className="w-[248px] p-3">
        <p className="m-0 mb-2 text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text-tertiary">Colores por estado</p>
        <ul className="m-0 flex list-none flex-col gap-[7px] p-0">
          {LEYENDA_ESTADOS.map(({ estado, hint }) => (
            <li key={estado} className="flex items-center gap-2">
              <EstadoChip estado={estado} className="flex-none" />
              <span className="min-w-0 text-[11px] leading-tight text-muted-foreground">{hint}</span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
